import type {
  CustomerBenefitCursor,
  CustomerBenefitProjectionPayload,
  CustomerBenefitRuntimeItem,
} from "../gateway/customer-benefit-client";
import { posRuntimeDb } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

const ZERO_UUID = "00000000-0000-0000-0000-000000000000";

export type LocalCustomerBenefitUsage = {
  sourceType: string;
  sourceInstanceId: string;
  customerId: string;
  rootRequestKey: string;
  localTransactionId: string;
  usedAt: string;
};

export function customerBenefitScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}:customer-benefit`;
}

function benefitKey(identity: TrustedTerminalIdentity, projectionId: string) {
  return `${customerBenefitScope(identity)}:benefit:${projectionId}`;
}

function cursorKey(identity: TrustedTerminalIdentity) {
  return `local-index:cursor:${customerBenefitScope(identity)}`;
}

function usageKey(identity: TrustedTerminalIdentity, sourceType: string, sourceInstanceId: string) {
  return `customer-benefit-use:${identity.organization_id}:${sourceType}:${sourceInstanceId}`;
}

export async function readCustomerBenefitCursor(identity: TrustedTerminalIdentity): Promise<CustomerBenefitCursor | null> {
  const row = await posRuntimeDb.runtimeMeta.get(cursorKey(identity));
  if (!row?.value || typeof row.value !== "object") return null;
  const value = row.value as Partial<CustomerBenefitCursor>;
  if (typeof value.changed_at !== "string" || typeof value.entity_id !== "string") return null;
  return { changed_at: value.changed_at, entity_id: value.entity_id };
}

async function writeCursor(identity: TrustedTerminalIdentity, cursor: CustomerBenefitCursor | null, serverAt: string) {
  const effective = cursor?.changed_at && cursor.entity_id
    ? cursor
    : { changed_at: serverAt, entity_id: ZERO_UUID };
  const now = new Date().toISOString();
  await posRuntimeDb.runtimeMeta.put({
    key: cursorKey(identity),
    scopeKey: customerBenefitScope(identity),
    value: effective,
    updatedAt: now,
  });
}

export async function applyCustomerBenefitRuntime(
  identity: TrustedTerminalIdentity,
  payload: CustomerBenefitProjectionPayload,
) {
  const scopeKey = customerBenefitScope(identity);
  await posRuntimeDb.transaction("rw", posRuntimeDb.projections, posRuntimeDb.runtimeMeta, async () => {
    if (payload.mode === "FULL" || payload.mode === "DISABLED") {
      await posRuntimeDb.projections.where("scopeKey").equals(scopeKey).delete();
    }
    if (payload.mode !== "DISABLED") {
      for (const item of payload.items) {
        const key = benefitKey(identity, item.projection_id);
        if (!item.active) {
          await posRuntimeDb.projections.delete(key);
          continue;
        }
        await posRuntimeDb.projections.put({
          key,
          scopeKey,
          domain: "CUSTOMER_BENEFIT",
          revision: `${item.state}:${item.changed_at}`,
          payload: item,
          updatedAt: item.changed_at,
        });
      }
    }
    await writeCursor(identity, payload.next_cursor, payload.server_at);
  });
}

function isBenefit(value: unknown): value is CustomerBenefitRuntimeItem {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<CustomerBenefitRuntimeItem>;
  return item.projection_kind === "CUSTOMER_BENEFIT"
    && typeof item.projection_id === "string"
    && typeof item.source_type === "string"
    && typeof item.source_instance_id === "string"
    && typeof item.customer_id === "string"
    && typeof item.state === "string"
    && item.active === true
    && typeof item.changed_at === "string"
    && Boolean(item.benefit_snapshot)
    && Boolean(item.policy_snapshot);
}

export async function hasLocalCustomerBenefitUsage(
  identity: TrustedTerminalIdentity,
  sourceType: string,
  sourceInstanceId: string,
) {
  return Boolean(await posRuntimeDb.journal.get(usageKey(identity, sourceType, sourceInstanceId)));
}

export async function readActiveCustomerBenefits(
  identity: TrustedTerminalIdentity,
  customerId: string,
): Promise<CustomerBenefitRuntimeItem[]> {
  const scopeKey = customerBenefitScope(identity);
  const rows = await posRuntimeDb.projections.where("scopeKey").equals(scopeKey).toArray();
  const benefits = rows.flatMap((row) => isBenefit(row.payload) && row.payload.customer_id === customerId ? [row.payload] : []);
  const visible: CustomerBenefitRuntimeItem[] = [];
  for (const item of benefits) {
    if (await hasLocalCustomerBenefitUsage(identity, item.source_type, item.source_instance_id)) continue;
    if (item.valid_until && Date.parse(item.valid_until) <= Date.now()) continue;
    visible.push(item);
  }
  return visible.sort((a, b) => {
    if (a.source_type !== b.source_type) return a.source_type.localeCompare(b.source_type);
    return a.changed_at.localeCompare(b.changed_at) || a.projection_id.localeCompare(b.projection_id);
  });
}

export async function writeLocalCustomerBenefitUsage(
  identity: TrustedTerminalIdentity,
  benefit: Pick<CustomerBenefitRuntimeItem, "source_type" | "source_instance_id" | "customer_id">,
  rootRequestKey: string,
  localTransactionId: string,
  usedAt: string,
) {
  const localId = usageKey(identity, benefit.source_type, benefit.source_instance_id);
  const existing = await posRuntimeDb.journal.get(localId);
  if (existing) {
    const value = existing.snapshot as Partial<LocalCustomerBenefitUsage>;
    if (value.rootRequestKey !== rootRequestKey || value.localTransactionId !== localTransactionId) {
      throw new Error("CUSTOMER_BENEFIT_ALREADY_USED_LOCALLY");
    }
    return;
  }
  const snapshot: LocalCustomerBenefitUsage = {
    sourceType: benefit.source_type,
    sourceInstanceId: benefit.source_instance_id,
    customerId: benefit.customer_id,
    rootRequestKey,
    localTransactionId,
    usedAt,
  };
  await posRuntimeDb.journal.put({
    localId,
    scopeKey: customerBenefitScope(identity),
    kind: "CUSTOMER_BENEFIT_USAGE",
    state: "USED_PENDING_SYNC",
    requestKey: `${rootRequestKey}:BENEFIT:${benefit.source_type}:${benefit.source_instance_id}`,
    snapshot,
    createdAt: usedAt,
    updatedAt: usedAt,
  });
}

export async function acknowledgeLocalCustomerBenefitUsage(
  identity: TrustedTerminalIdentity,
  sourceType: string,
  sourceInstanceId: string,
) {
  const localId = usageKey(identity, sourceType, sourceInstanceId);
  const benefitRows = await posRuntimeDb.projections
    .where("scopeKey")
    .equals(customerBenefitScope(identity))
    .filter((row) => {
      if (!isBenefit(row.payload)) return false;
      return row.payload.source_type === sourceType && row.payload.source_instance_id === sourceInstanceId;
    })
    .primaryKeys();
  await posRuntimeDb.transaction("rw", posRuntimeDb.projections, posRuntimeDb.journal, async () => {
    await posRuntimeDb.projections.bulkDelete(benefitRows as string[]);
    await posRuntimeDb.journal.delete(localId);
  });
}

export async function purgeCustomerBenefitsOutsideScope(identity: TrustedTerminalIdentity) {
  const keepPrefix = `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}:`;
  await Promise.all([
    posRuntimeDb.projections.filter((row) => row.domain === "CUSTOMER_BENEFIT" && !row.scopeKey.startsWith(keepPrefix)).delete(),
    posRuntimeDb.runtimeMeta.filter((row) => row.key.includes(":customer-benefit") && !row.scopeKey.startsWith(keepPrefix)).delete(),
  ]);
}
